import { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { X, Loader2, ExternalLink, Check, AlertCircle, Search, Disc, DollarSign, Save, Edit2, MapPin, ChevronLeft, ChevronRight, RotateCcw, RotateCw, ZoomIn, Image as ImageIcon, ChevronDown, ChevronUp, Truck, Package, CheckCircle, MessageCircle, Share2, Link2, Send, Copy, Trash2, Mail, Upload, RefreshCw } from 'lucide-react';
import { api } from '../../services/api';
import { ImageLightbox } from '../common/ImageLightbox';
import { ItemEditModal } from '../common/ItemEditModal';
import { useToast } from '../common/Toast';
import {
  DISCOGS_MEDIA_CONDITIONS,
  DISCOGS_SLEEVE_CONDITIONS,
  SHIPPING_CARRIERS,
  type ItemWithImages,
  type DiscogsMediaCondition,
  type DiscogsSleeveCondition,
  type DiscogsRelease,
  type ShippingCarrierId,
} from '@findmystuff/shared';

interface PriceSuggestion {
  condition: string;
  value: number | null;
  currency: string;
}

interface DiscogsSellModalProps {
  item: ItemWithImages;
  isOpen: boolean;
  onClose: () => void;
}

export function DiscogsSellModal({ item, isOpen, onClose }: DiscogsSellModalProps) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // Fetch full item data with all images
  const { data: fullItem, isLoading: isLoadingItem } = useQuery({
    queryKey: ['item', item.id],
    queryFn: () => api.items.get(item.id),
    enabled: isOpen,
  });

  // Fetch existing Discogs listings for this item
  const { data: existingListings } = useQuery({
    queryKey: ['discogs-listings', item.id],
    queryFn: () => api.discogs.getListings(item.id),
    enabled: isOpen,
  });

  // Fetch order info (messages + buyer) for sold items
  const { data: orderInfo, isLoading: isLoadingOrderInfo } = useQuery({
    queryKey: ['discogs-order-info', item.id],
    queryFn: () => api.discogs.getItemOrderInfo(item.id),
    enabled: isOpen && !!item.soldAt, // Only fetch for sold items
  });

  // Fetch linked emails for sold items
  const { data: linkedEmails, isLoading: isLoadingEmails } = useQuery({
    queryKey: ['item-emails', item.id],
    queryFn: () => api.email.getEmailsForItem(item.id),
    enabled: isOpen && !!item.soldAt, // Only fetch for sold items
  });

  // Get the most recent listing (first one, since they're sorted by createdAt desc)
  const existingListing = existingListings?.[0];

  // Use full item data if available, otherwise fall back to prop item
  const itemData = fullItem || item;

  // Item display state (editable title/description)
  const [itemTitle, setItemTitle] = useState(item.title);
  const [itemDescription, setItemDescription] = useState(item.description || '');

  // Form state
  const [releaseSearch, setReleaseSearch] = useState('');
  const [selectedRelease, setSelectedRelease] = useState<DiscogsRelease | null>(null);
  const [mediaCondition, setMediaCondition] = useState<DiscogsMediaCondition>('Very Good Plus (VG+)');
  const [sleeveCondition, setSleeveCondition] = useState<DiscogsSleeveCondition>('Very Good Plus (VG+)');
  const [price, setPrice] = useState('');
  const [comments, setComments] = useState('');

  // Search state
  const [searchResults, setSearchResults] = useState<DiscogsRelease[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showAllResults, setShowAllResults] = useState(false);

  // Price suggestions state
  const [priceSuggestions, setPriceSuggestions] = useState<PriceSuggestion[]>([]);
  const [isLoadingPrices, setIsLoadingPrices] = useState(false);

  // Track saved release ID to detect when selecting the same release
  const [savedReleaseId, setSavedReleaseId] = useState<number | null>(null);

  // Flag to skip auto-updating price when we have saved values
  const [skipPriceAutoUpdate, setSkipPriceAutoUpdate] = useState(false);

  // Success state
  const [listingResult, setListingResult] = useState<{ listingId: string; listingUrl: string } | null>(null);

  // Use listingResult OR existingListing to determine if listed (for immediate feedback after listing)
  const isListed = !!listingResult || !!existingListing;
  const listingUrl = listingResult?.listingUrl || existingListing?.discogsUrl;

  // Detect if listing fields have changed from existing listing values
  const listingHasChanges = isListed && !!existingListing && (
    mediaCondition !== existingListing.mediaCondition ||
    sleeveCondition !== existingListing.sleeveCondition ||
    (price !== '' && parseFloat(price) !== existingListing.price) ||
    (comments || '') !== (existingListing.comments || '')
  );

  // Track if there are unsaved changes to title/description
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Image lightbox state
  const [showImageLightbox, setShowImageLightbox] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);

  // Gallery state for two-column layout
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Gallery zoom state
  const [galleryZoom, setGalleryZoom] = useState(100); // 100%, 200%, 300%, 400%, 500%
  const [galleryPosition, setGalleryPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // High-res image state
  const [showingHighRes, setShowingHighRes] = useState(false);
  const [highResLoading, setHighResLoading] = useState(false);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);

  // Shipping tracking state
  const [isShippingExpanded, setIsShippingExpanded] = useState(false);
  const [shippingTrackingNumber, setShippingTrackingNumber] = useState('');
  const [shippingCarrier, setShippingCarrier] = useState<ShippingCarrierId>('canadapost');
  const [isEditingShipping, setIsEditingShipping] = useState(false);

  // Share link state
  const [isShareExpanded, setIsShareExpanded] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copiedShare, setCopiedShare] = useState(false);

  // ImgBB state
  const [isImgbbExpanded, setIsImgbbExpanded] = useState(false);
  const [imgbbUrls, setImgbbUrls] = useState<string[]>([]);
  const [copiedImgbbIndex, setCopiedImgbbIndex] = useState<number | null>(null);

  // Reply message state
  const [replyMessage, setReplyMessage] = useState('');

  // Get all images from the full item data
  const allImages = itemData.images || [];
  const primaryImage = allImages[0];

  // Lightbox navigation
  const openLightbox = (index: number = 0) => {
    setLightboxImageIndex(index);
    setShowImageLightbox(true);
  };

  // Handle edit modal close - refresh item data
  const handleEditModalClose = () => {
    setShowEditModal(false);
    // Refresh item data when edit modal closes
    queryClient.invalidateQueries({ queryKey: ['item', item.id] });
  };

  // Track if we've initialized from fullItem to avoid re-initializing
  const [initializedFromFullItem, setInitializedFromFullItem] = useState(false);

  // Email notifications - expanded state
  const [expandedEmailId, setExpandedEmailId] = useState<string | null>(null);

  // Reset initialization flag when item changes
  useEffect(() => {
    setInitializedFromFullItem(false);
    setListingResult(null);
  }, [item.id]);

  // Initialize form when fullItem loads (has all discogs fields)
  useEffect(() => {
    // Only initialize once per item, and only when fullItem is available
    if (!fullItem || initializedFromFullItem) {
      return;
    }

    setInitializedFromFullItem(true);
    setItemTitle(fullItem.title);
    setItemDescription(fullItem.description || '');
    setHasUnsavedChanges(false);
    setSaveSuccess(false);
    setShowSearchResults(false);

    // Load saved Discogs fields if available
    // Default comment for "new" or "like new" items
    const sealedComment = 'Still in original plastic wrap. Never opened.';
    const isSealedCondition = fullItem.condition === 'new' || fullItem.condition === 'like_new';

    if (fullItem.discogsReleaseId) {
      // We have saved Discogs data - restore it
      setSavedReleaseId(fullItem.discogsReleaseId);
      setSkipPriceAutoUpdate(true); // Don't auto-update price/conditions - we have saved values
      setPrice(fullItem.discogsPrice?.toString() || '');
      setMediaCondition((fullItem.discogsMediaCondition as DiscogsMediaCondition) || 'Very Good Plus (VG+)');
      setSleeveCondition((fullItem.discogsSleeveCondition as DiscogsSleeveCondition) || 'Very Good Plus (VG+)');
      // Use saved comments, or autofill for "new"/"like new" items if no saved comments
      const savedComments = fullItem.discogsComments || '';
      setComments(savedComments || (isSealedCondition ? sealedComment : ''));

      // Check if we have label/catno data saved, if not fetch from Discogs
      const hasLabelData = fullItem.discogsLabel || fullItem.discogsCatno;

      if (hasLabelData) {
        // Create release object from saved data
        const titleParts = fullItem.title.split(' - ');
        const restoredRelease: DiscogsRelease = {
          id: fullItem.discogsReleaseId,
          title: titleParts.length > 1 ? titleParts.slice(1).join(' - ') : fullItem.title,
          artist: titleParts.length > 1 ? (titleParts[0] || '') : '',
          year: null,
          format: [],
          label: fullItem.discogsLabel ? fullItem.discogsLabel.split(', ') : [],
          catno: fullItem.discogsCatno || null,
          country: null,
          thumb: '',
          coverImage: '',
          resourceUrl: '',
        };
        setSelectedRelease(restoredRelease);
        setReleaseSearch(`${restoredRelease.artist} - ${restoredRelease.title}`);
      } else {
        // Fetch release details from Discogs to get label/catno
        const titleParts = fullItem.title.split(' - ');
        const tempRelease: DiscogsRelease = {
          id: fullItem.discogsReleaseId,
          title: titleParts.length > 1 ? titleParts.slice(1).join(' - ') : fullItem.title,
          artist: titleParts.length > 1 ? (titleParts[0] || '') : '',
          year: null,
          format: [],
          label: [],
          catno: null,
          country: null,
          thumb: '',
          coverImage: '',
          resourceUrl: '',
        };
        setSelectedRelease(tempRelease);
        setReleaseSearch(`${tempRelease.artist} - ${tempRelease.title}`);

        // Fetch full release details async
        api.discogs.getRelease(fullItem.discogsReleaseId).then((release) => {
          setSelectedRelease(release);
          setReleaseSearch(`${release.artist} - ${release.title}`);
        }).catch((err) => {
          console.error('Failed to fetch release details:', err);
        });
      }
      setPriceSuggestions([]);
    } else {
      setSavedReleaseId(null);
      setSkipPriceAutoUpdate(false); // Allow auto-updating since no saved values
      // No saved Discogs data - reset to defaults
      setReleaseSearch(fullItem.title);
      setSelectedRelease(null);
      setPriceSuggestions([]);
      setPrice('');
      setMediaCondition('Very Good Plus (VG+)');
      setSleeveCondition('Very Good Plus (VG+)');
      // Autofill comments for "new" or "like new" items
      setComments(isSealedCondition ? sealedComment : '');
    }

    // Initialize shipping fields
    const itemWithShipping = fullItem as typeof fullItem & {
      trackingNumber?: string | null;
      trackingCarrier?: string | null;
      shippedAt?: Date | null;
      shareToken?: string | null;
      imgbbImageUrls?: string[] | null;
    };
    setShippingTrackingNumber(itemWithShipping.trackingNumber || '');
    setShippingCarrier((itemWithShipping.trackingCarrier as ShippingCarrierId) || 'canadapost');
    setIsEditingShipping(false);
    // Auto-expand shipping section if item is sold
    setIsShippingExpanded(!!fullItem.soldAt);

    // Initialize share URL if share token exists
    if (itemWithShipping.shareToken) {
      setShareUrl(`${window.location.origin}/share/${itemWithShipping.shareToken}`);
    } else {
      setShareUrl(null);
    }
    setIsShareExpanded(false);
    setCopiedShare(false);

    // Initialize ImgBB URLs if they exist
    if (itemWithShipping.imgbbImageUrls && itemWithShipping.imgbbImageUrls.length > 0) {
      setImgbbUrls(itemWithShipping.imgbbImageUrls);
    } else {
      setImgbbUrls([]);
    }
    setIsImgbbExpanded(false);
    setCopiedImgbbIndex(null);

    setReplyMessage('');
  }, [fullItem, initializedFromFullItem]);

  // Fill in form values from existingListing when item's Discogs fields are missing
  // (handles items listed before we started saving these fields on the item record)
  useEffect(() => {
    if (!existingListing || !initializedFromFullItem) return;
    if (fullItem?.discogsPrice != null) return; // Item already has the fields, no need
    setPrice(String(existingListing.price));
    setMediaCondition(existingListing.mediaCondition as DiscogsMediaCondition);
    setSleeveCondition(existingListing.sleeveCondition as DiscogsSleeveCondition);
    setComments(existingListing.comments || '');
  }, [existingListing, initializedFromFullItem, fullItem?.discogsPrice]);

  // Debounced search - only run when no release is selected
  useEffect(() => {
    // Don't search if we already have a selected release
    if (selectedRelease) {
      return;
    }

    if (releaseSearch.length < 3) {
      setSearchResults([]);
      setShowAllResults(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await api.discogs.searchReleases(releaseSearch);
        setSearchResults(results);
        setShowSearchResults(true);
        setShowAllResults(false); // Reset expansion when new results arrive
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [releaseSearch, selectedRelease]);

  // Update item mutation (for updating title/description and Discogs fields)
  const updateItemMutation = useMutation({
    mutationFn: (data: {
      title: string;
      description: string;
      discogsReleaseId?: number | null;
      discogsPrice?: number | null;
      discogsMediaCondition?: string | null;
      discogsSleeveCondition?: string | null;
      discogsComments?: string | null;
    }) => {
      return api.items.update(item.id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['item', item.id] });
      setHasUnsavedChanges(false);
      setSaveSuccess(true);
      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  // Create listing mutation
  const createListingMutation = useMutation({
    mutationFn: () =>
      api.discogs.createListing({
        itemId: item.id,
        releaseId: selectedRelease!.id,
        mediaCondition,
        sleeveCondition,
        price: parseFloat(price),
        comments: comments || undefined,
      }),
    onSuccess: (data) => {
      setListingResult(data);
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['discogs-listings', item.id] });
      // Show success toast
      showToast({
        type: 'success',
        message: `Listed on Discogs for $${price}`,
        link: data.listingUrl ? {
          url: data.listingUrl,
          text: 'View on Discogs',
        } : undefined,
      });
    },
  });

  // Delete listing mutation
  const deleteListingMutation = useMutation({
    mutationFn: () => api.discogs.deleteListing(item.id),
    onSuccess: () => {
      // Reset local state
      setListingResult(null);
      setSelectedRelease(null);
      setPrice('');
      setComments('');
      setMediaCondition('Very Good Plus (VG+)');
      setSleeveCondition('Very Good Plus (VG+)');
      setSavedReleaseId(null);
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['item', item.id] });
      queryClient.invalidateQueries({ queryKey: ['discogs-listings', item.id] });
      // Show success toast
      showToast({
        type: 'success',
        message: 'Listing removed from Discogs',
      });
    },
  });

  // Update listing mutation
  const updateListingMutation = useMutation({
    mutationFn: () =>
      api.discogs.updateListing(item.id, {
        mediaCondition,
        sleeveCondition,
        price: parseFloat(price),
        comments: comments || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discogs-listings', item.id] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['item', item.id] });
      showToast({
        type: 'success',
        message: 'Listing updated on Discogs',
      });
    },
    onError: (error: Error) => {
      showToast({
        type: 'error',
        message: error.message || 'Failed to update listing on Discogs. Please try again.',
      });
    },
  });

  // Handle delist confirmation
  const handleDelist = () => {
    if (confirm('Are you sure you want to remove this listing from Discogs? This will delete the listing and reset all Discogs data for this item.')) {
      deleteListingMutation.mutate();
    }
  };

  // Rotation mutation for gallery images
  const rotateImageMutation = useMutation({
    mutationFn: ({ imageId, direction }: { imageId: string; direction: 'left' | 'right' }) =>
      api.items.rotateImage(item.id, imageId, direction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', item.id] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });

  // Generate share link mutation
  const generateShareLinkMutation = useMutation({
    mutationFn: () => api.items.generateShareLink(item.id),
    onSuccess: (data) => {
      const url = `${window.location.origin}/share/${data.shareToken}`;
      setShareUrl(url);
      queryClient.invalidateQueries({ queryKey: ['item', item.id] });
    },
  });

  // Revoke share link mutation
  const revokeShareLinkMutation = useMutation({
    mutationFn: () => api.items.revokeShareLink(item.id),
    onSuccess: () => {
      setShareUrl(null);
      queryClient.invalidateQueries({ queryKey: ['item', item.id] });
    },
  });

  // Upload to ImgBB mutation
  const uploadToImgbbMutation = useMutation({
    mutationFn: () => api.items.uploadToImgbb(item.id),
    onSuccess: (data) => {
      setImgbbUrls(data.imageUrls);
      queryClient.invalidateQueries({ queryKey: ['item', item.id] });
      showToast({
        type: 'success',
        message: `${data.imageUrls.length} image${data.imageUrls.length > 1 ? 's' : ''} uploaded to ImgBB`,
        link: data.imageUrls[0] ? {
          url: data.imageUrls[0],
          text: 'View image',
        } : undefined,
      });
    },
    onError: (error: Error) => {
      showToast({
        type: 'error',
        message: error.message || 'Failed to upload images to ImgBB',
      });
    },
  });

  // Delete ImgBB images mutation
  const deleteImgbbImagesMutation = useMutation({
    mutationFn: () => api.items.deleteImgbbImages(item.id),
    onSuccess: () => {
      setImgbbUrls([]);
      queryClient.invalidateQueries({ queryKey: ['item', item.id] });
      showToast({
        type: 'success',
        message: 'ImgBB images cleared',
      });
    },
    onError: (error: Error) => {
      showToast({
        type: 'error',
        message: error.message || 'Failed to clear ImgBB images',
      });
    },
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: (message: string) =>
      api.discogs.sendMessage(orderInfo?.orderId || '', message),
    onSuccess: () => {
      setReplyMessage('');
      queryClient.invalidateQueries({ queryKey: ['discogs-order-info', item.id] });
    },
  });

  const handleSelectRelease = async (release: DiscogsRelease) => {
    // Check if we're selecting the same release that was already saved
    const isSameRelease = savedReleaseId !== null && savedReleaseId === release.id;

    setSelectedRelease(release);
    setReleaseSearch(`${release.artist} - ${release.title}`);
    setShowSearchResults(false);

    // If selecting the same release that was saved, don't change ANY fields
    // Just update the release object to show full details
    if (isSameRelease) {
      // Don't fetch anything, don't update anything - just show the release
      // All saved fields (price, conditions, title, description) stay as-is
      return;
    }

    // Selecting a DIFFERENT release - update title/description and mark as unsaved
    // Allow auto-updating price since this is a new release
    setSkipPriceAutoUpdate(false);

    // Build new title from Discogs release (max 200 chars for validation)
    const fullTitle = `${release.artist} - ${release.title}`;
    const newTitle = fullTitle.length > 200 ? fullTitle.substring(0, 197) + '...' : fullTitle;

    // Build description from Discogs info
    const descriptionParts: string[] = [];
    if (release.year) {
      descriptionParts.push(`Year: ${release.year}`);
    }
    if (release.format.length > 0) {
      descriptionParts.push(`Format: ${release.format.join(', ')}`);
    }
    if (release.label.length > 0) {
      descriptionParts.push(`Label: ${release.label.join(', ')}`);
    }
    if (release.country) {
      descriptionParts.push(`Country: ${release.country}`);
    }
    const discogsInfo = descriptionParts.join('\n');

    // Append existing description with star demarcation if it exists (max 2000 chars)
    const existingDesc = itemData.description?.trim();
    let newDescription = existingDesc
      ? `${discogsInfo}\n\n********************\n\n${existingDesc}`
      : discogsInfo;

    // Truncate if too long
    if (newDescription.length > 2000) {
      newDescription = newDescription.substring(0, 1997) + '...';
    }

    // Update local state only - don't save yet
    setItemTitle(newTitle);
    setItemDescription(newDescription);
    setHasUnsavedChanges(true);
    setSaveSuccess(false);

    // Fetch price suggestions for this release
    setIsLoadingPrices(true);
    setPriceSuggestions([]);
    try {
      const suggestions = await api.discogs.getPriceSuggestions(release.id);
      setPriceSuggestions(suggestions);

      // Auto-populate price based on current media condition (only for new release)
      const currentConditionPrice = suggestions.find(s => s.condition === mediaCondition);
      if (currentConditionPrice?.value) {
        setPrice(currentConditionPrice.value.toFixed(2));
      }
    } catch (error) {
      console.error('Failed to fetch price suggestions:', error);
    } finally {
      setIsLoadingPrices(false);
    }
  };

  // Update price when media condition changes (if we have suggestions and not skipping)
  useEffect(() => {
    // Don't auto-update if we have saved values we want to preserve
    if (skipPriceAutoUpdate) {
      return;
    }
    if (priceSuggestions.length > 0) {
      const conditionPrice = priceSuggestions.find(s => s.condition === mediaCondition);
      if (conditionPrice?.value) {
        setPrice(conditionPrice.value.toFixed(2));
      }
    }
  }, [mediaCondition, priceSuggestions, skipPriceAutoUpdate]);

  // Build save data object with all Discogs fields
  const buildSaveData = () => ({
    title: itemTitle,
    description: itemDescription,
    discogsReleaseId: selectedRelease?.id || null,
    discogsLabel: selectedRelease?.label?.join(', ') || null,
    discogsCatno: selectedRelease?.catno || null,
    discogsPrice: price ? parseFloat(price) : null,
    discogsMediaCondition: selectedRelease ? mediaCondition : null,
    discogsSleeveCondition: selectedRelease ? sleeveCondition : null,
    discogsComments: comments || null,
  });

  // Save item data (title/description and Discogs fields)
  const handleSave = () => {
    if (!hasUnsavedChanges) return;
    updateItemMutation.mutate(buildSaveData());
  };

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    // Save item data first if there are unsaved changes
    if (hasUnsavedChanges) {
      await updateItemMutation.mutateAsync(buildSaveData());
    }
    createListingMutation.mutate();
  };

  const handleClose = () => {
    setListingResult(null);
    setSelectedRelease(null);
    setReleaseSearch(item.title);
    setSearchResults([]);
    setPriceSuggestions([]);
    setPrice('');
    onClose();
  };

  const isValid =
    selectedRelease &&
    parseFloat(price) > 0;

  // Shipping status helpers
  const itemWithShipping = fullItem as (typeof fullItem & {
    shippedAt?: Date | string | null;
    deliveredAt?: Date | string | null;
    trackingNumber?: string | null;
    trackingCarrier?: string | null;
    shippingSource?: 'discogs' | 'manual' | null;
  }) | undefined;
  const isShipped = !!itemWithShipping?.shippedAt;
  const isDelivered = !!itemWithShipping?.deliveredAt;
  const isSold = !!fullItem?.soldAt;
  const isFromDiscogs = itemWithShipping?.shippingSource === 'discogs';

  // Shipping handlers
  const handleMarkAsShipped = async () => {
    try {
      // Update local item
      await api.items.update(item.id, {
        shippedAt: new Date(),
        trackingNumber: shippingTrackingNumber || null,
        trackingCarrier: shippingCarrier || null,
        shippingSource: 'manual',
        listingStatus: 'sold', // Ensure item stays in sold filter
      });

      // Also update Discogs order if this item has one
      if (orderInfo?.orderId) {
        try {
          const carrierName = SHIPPING_CARRIERS.find(c => c.id === shippingCarrier)?.name;
          await api.discogs.updateOrderShipping(
            orderInfo.orderId,
            shippingTrackingNumber || undefined,
            carrierName
          );
        } catch (discogsError) {
          console.error('Failed to update Discogs order (local update succeeded):', discogsError);
          // Don't fail the whole operation if Discogs update fails
        }
      }

      queryClient.invalidateQueries({ queryKey: ['item', item.id] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['discogs-order-info', item.id] });
      setIsEditingShipping(false);
    } catch (error) {
      console.error('Failed to mark as shipped:', error);
    }
  };

  const handleUpdateTracking = async () => {
    try {
      // Update local item
      await api.items.update(item.id, {
        trackingNumber: shippingTrackingNumber || null,
        trackingCarrier: shippingCarrier || null,
        shippingSource: 'manual', // User is taking over, mark as manual
        listingStatus: 'sold', // Ensure item stays in sold filter
      });

      // Also update Discogs order tracking if this item has one
      if (orderInfo?.orderId) {
        try {
          const carrierName = SHIPPING_CARRIERS.find(c => c.id === shippingCarrier)?.name;
          await api.discogs.updateOrderShipping(
            orderInfo.orderId,
            shippingTrackingNumber || undefined,
            carrierName
          );
        } catch (discogsError) {
          console.error('Failed to update Discogs order tracking (local update succeeded):', discogsError);
        }
      }

      queryClient.invalidateQueries({ queryKey: ['item', item.id] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['discogs-order-info', item.id] });
      setIsEditingShipping(false);
    } catch (error) {
      console.error('Failed to update tracking:', error);
    }
  };

  const handleMarkAsDelivered = async () => {
    try {
      await api.items.update(item.id, {
        deliveredAt: new Date(),
        listingStatus: 'sold', // Ensure item stays in sold filter
      });
      queryClient.invalidateQueries({ queryKey: ['item', item.id] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
    } catch (error) {
      console.error('Failed to mark as delivered:', error);
    }
  };

  // Thumbnail strip ref for scrolling into view
  const thumbnailStripRef = useRef<HTMLDivElement>(null);
  const scrollThumbnailIntoView = useCallback((index: number) => {
    const strip = thumbnailStripRef.current;
    if (!strip) return;
    const thumb = strip.children[index] as HTMLElement | undefined;
    if (thumb) {
      thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, []);

  // Navigate to previous/next image in gallery
  const goToPrevImage = () => {
    const newIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : allImages.length - 1;
    setSelectedImageIndex(newIndex);
    setGalleryZoom(100);
    setGalleryPosition({ x: 0, y: 0 });
    setShowingHighRes(false);
    scrollThumbnailIntoView(newIndex);
  };
  const goToNextImage = () => {
    const newIndex = selectedImageIndex < allImages.length - 1 ? selectedImageIndex + 1 : 0;
    setSelectedImageIndex(newIndex);
    setGalleryZoom(100);
    setGalleryPosition({ x: 0, y: 0 });
    setShowingHighRes(false);
    scrollThumbnailIntoView(newIndex);
  };

  // Toggle high-res image
  const toggleHighRes = () => {
    if (!currentGalleryImage?.originalUrl) return;
    if (!showingHighRes) {
      setHighResLoading(true);
    }
    setShowingHighRes(!showingHighRes);
    // Reset zoom when toggling
    setGalleryZoom(100);
    setGalleryPosition({ x: 0, y: 0 });
  };

  const handleHighResLoad = () => {
    setHighResLoading(false);
  };

  // Gallery zoom levels cycle: 100% -> 250% -> 500% -> 1000% -> 100%
  const zoomLevels = [100, 250, 500, 1000] as const;
  const handleGalleryDoubleClick = (e?: React.MouseEvent) => {
    const currentIndex = zoomLevels.indexOf(galleryZoom as typeof zoomLevels[number]);
    if (currentIndex === -1 || currentIndex === zoomLevels.length - 1) {
      // Not in list or at max, reset to 100%
      setGalleryZoom(100);
      setGalleryPosition({ x: 0, y: 0 });
    } else {
      // Go to next zoom level
      const nextZoom = zoomLevels[currentIndex + 1];
      if (nextZoom !== undefined) {
        // If we have a click event, zoom centered on click position
        if (e && e.currentTarget) {
          const rect = e.currentTarget.getBoundingClientRect();
          // Click position relative to container center
          const clickX = e.clientX - rect.left - rect.width / 2;
          const clickY = e.clientY - rect.top - rect.height / 2;

          // Calculate new position to keep the clicked point stationary on screen
          // Formula: newPos = clickPos - (clickPos - currentPos) * (newZoom / oldZoom)
          const zoomRatio = nextZoom / galleryZoom;
          const newX = clickX - (clickX - galleryPosition.x) * zoomRatio;
          const newY = clickY - (clickY - galleryPosition.y) * zoomRatio;

          setGalleryPosition({ x: newX, y: newY });
        }
        setGalleryZoom(nextZoom);
      }
    }
  };

  // Gallery pan handlers
  const handleGalleryMouseDown = (e: React.MouseEvent) => {
    if (galleryZoom > 100) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX - galleryPosition.x, y: e.clientY - galleryPosition.y });
    }
  };

  const handleGalleryMouseMove = (e: React.MouseEvent) => {
    if (isDragging && galleryZoom > 100) {
      setGalleryPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleGalleryMouseUp = () => {
    setIsDragging(false);
  };

  // Get current gallery image
  const currentGalleryImage = allImages[selectedImageIndex];
  const canRotateGallery = !!currentGalleryImage?.id;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />

      {/* Modal - wider on desktop for two-column layout */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg lg:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
            <div className="flex items-center gap-2">
              <Disc className="w-5 h-5 text-gray-700" />
              <h2 className="text-lg font-semibold">Sell on Discogs</h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content - Two column layout on desktop */}
          <div className="flex-1 overflow-y-auto lg:overflow-hidden lg:flex">
            {isLoadingItem || !initializedFromFullItem ? (
              // Loading state - wait for item data before showing form
              <div className="flex flex-col items-center justify-center py-12 w-full">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400 mb-4" />
                <p className="text-gray-500">Loading item data...</p>
              </div>
            ) : (
              <>
                {/* Left Column - Image Gallery (Desktop only) */}
                {allImages.length > 0 && (
                  <div className="hidden lg:flex lg:flex-col lg:w-1/2 lg:border-r lg:bg-gray-900">
                    {/* Controls Bar */}
                    <div className="flex items-center justify-between p-2 bg-gray-800 text-white">
                      <div className="flex items-center gap-2">
                        {/* Rotation buttons */}
                        {canRotateGallery && (
                          <>
                            <button
                              type="button"
                              onClick={() => currentGalleryImage?.id && rotateImageMutation.mutate({ imageId: currentGalleryImage.id, direction: 'left' })}
                              disabled={rotateImageMutation.isPending}
                              className="p-2 hover:bg-white/20 rounded-full transition-colors disabled:opacity-50"
                              title="Rotate left 90°"
                            >
                              {rotateImageMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RotateCcw className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => currentGalleryImage?.id && rotateImageMutation.mutate({ imageId: currentGalleryImage.id, direction: 'right' })}
                              disabled={rotateImageMutation.isPending}
                              className="p-2 hover:bg-white/20 rounded-full transition-colors disabled:opacity-50"
                              title="Rotate right 90°"
                            >
                              <RotateCw className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>

                      {/* Zoom indicator */}
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleGalleryDoubleClick}
                          className="p-1.5 hover:bg-white/20 rounded-full transition-colors cursor-zoom-in"
                          title="Click to zoom"
                        >
                          <ZoomIn className="w-4 h-4" />
                        </button>
                        <span className="text-sm font-medium min-w-[50px] text-center">{galleryZoom}%</span>
                        {galleryZoom > 100 && (
                          <button
                            type="button"
                            onClick={() => { setGalleryZoom(100); setGalleryPosition({ x: 0, y: 0 }); }}
                            className="text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded transition-colors"
                          >
                            Reset
                          </button>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {/* High-res button */}
                        {currentGalleryImage?.originalUrl && (
                          <button
                            type="button"
                            onClick={toggleHighRes}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full transition-colors text-xs font-medium ${
                              showingHighRes
                                ? 'bg-emerald-500 text-white'
                                : 'bg-white/20 hover:bg-white/30 text-white'
                            }`}
                            title={showingHighRes ? 'View optimized image' : 'View full resolution'}
                          >
                            <ImageIcon className="w-3.5 h-3.5" />
                            {showingHighRes ? 'Optimized' : 'Full Res'}
                          </button>
                        )}

                        {/* Image counter */}
                        {allImages.length > 1 && (
                          <span className="text-sm text-gray-400">
                            {selectedImageIndex + 1} / {allImages.length}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Main Image Display */}
                    <div
                      className="flex-1 flex items-center justify-center p-4 relative overflow-hidden"
                      onMouseDown={handleGalleryMouseDown}
                      onMouseMove={handleGalleryMouseMove}
                      onMouseUp={handleGalleryMouseUp}
                      onMouseLeave={handleGalleryMouseUp}
                      onDoubleClick={handleGalleryDoubleClick}
                      style={{
                        cursor: galleryZoom > 100 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
                      }}
                    >
                      {/* Loading indicator for high-res */}
                      {highResLoading && showingHighRes && (
                        <div className="absolute inset-0 flex items-center justify-center z-20 bg-black/50">
                          <div className="flex flex-col items-center gap-3">
                            <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                            <span className="text-white text-sm">Loading full resolution...</span>
                          </div>
                        </div>
                      )}

                      <img
                        src={showingHighRes && currentGalleryImage?.originalUrl
                          ? currentGalleryImage.originalUrl
                          : (allImages[selectedImageIndex]?.url || allImages[selectedImageIndex]?.thumbnailUrl)}
                        alt={`${itemTitle} - Image ${selectedImageIndex + 1}`}
                        className="max-w-full max-h-[55vh] object-contain select-none"
                        style={{
                          transform: `scale(${galleryZoom / 100}) translate(${galleryPosition.x / (galleryZoom / 100)}px, ${galleryPosition.y / (galleryZoom / 100)}px)`,
                          transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                        }}
                        draggable={false}
                        onLoad={showingHighRes ? handleHighResLoad : undefined}
                      />

                      {/* Zoom hint overlay - clickable */}
                      {galleryZoom === 100 && !showingHighRes && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleGalleryDoubleClick(); }}
                          className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 hover:text-white text-xs bg-black/60 hover:bg-black/80 px-3 py-1.5 rounded-full cursor-zoom-in transition-colors flex items-center gap-1.5"
                        >
                          <ZoomIn className="w-3.5 h-3.5" />
                          Click to zoom
                        </button>
                      )}

                      {/* Full resolution indicator */}
                      {showingHighRes && !highResLoading && (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-emerald-400 text-xs bg-black/60 px-3 py-1.5 rounded-full pointer-events-none flex items-center gap-1.5">
                          <ImageIcon className="w-3.5 h-3.5" />
                          Viewing full resolution
                        </div>
                      )}

                      {/* Navigation Arrows */}
                      {allImages.length > 1 && (
                        <>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); goToPrevImage(); }}
                            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full shadow-md transition-colors text-white"
                          >
                            <ChevronLeft className="w-6 h-6" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); goToNextImage(); }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full shadow-md transition-colors text-white"
                          >
                            <ChevronRight className="w-6 h-6" />
                          </button>
                        </>
                      )}
                    </div>

                    {/* Thumbnail Strip */}
                    {allImages.length > 1 && (
                      <div className="p-3 border-t border-gray-700 bg-gray-800">
                        <div ref={thumbnailStripRef} className="flex gap-2 overflow-x-auto pb-1 mx-auto w-fit max-w-full">
                          {allImages.map((img, index) => (
                            <button
                              key={img.id || index}
                              type="button"
                              onClick={() => {
                                setSelectedImageIndex(index);
                                setGalleryZoom(100);
                                setGalleryPosition({ x: 0, y: 0 });
                                setShowingHighRes(false);
                              }}
                              className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                                index === selectedImageIndex
                                  ? 'border-white opacity-100'
                                  : 'border-transparent opacity-60 hover:opacity-90'
                              }`}
                            >
                              <img
                                src={img.thumbnailUrl || img.url}
                                alt={`Thumbnail ${index + 1}`}
                                className="w-full h-full object-cover"
                                draggable={false}
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Right Column - Form (scrollable on desktop) */}
                <div className={`lg:overflow-y-auto ${allImages.length > 0 ? 'lg:w-1/2' : 'w-full'}`}>
                  <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    {/* Item Preview Section - Compact on desktop since images are in left column */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Item Details</span>
                        <button
                          type="button"
                          onClick={() => setShowEditModal(true)}
                          className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit Item
                        </button>
                      </div>

                      {/* Mobile: Show image thumbnail */}
                      <div className="flex gap-4 lg:hidden">
                        {primaryImage && (
                          <div className="flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => openLightbox(0)}
                              className="relative group cursor-zoom-in"
                            >
                              <img
                                src={primaryImage.url || primaryImage.thumbnailUrl}
                                alt={itemTitle}
                                className="w-32 h-32 object-cover rounded-lg shadow-sm transition-opacity group-hover:opacity-90"
                              />
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 rounded-lg">
                                <span className="text-white text-xs font-medium bg-black/50 px-2 py-1 rounded">
                                  {allImages.length > 1 ? `View ${allImages.length} photos` : 'Click to enlarge'}
                                </span>
                              </div>
                            </button>
                          </div>
                        )}

                        {/* Title and Description - Mobile layout */}
                        <div className="flex-1 min-w-0 space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Title
                            </label>
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {itemTitle}
                            </p>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Description
                            </label>
                            <textarea
                              value={itemDescription}
                              onChange={(e) => {
                                setItemDescription(e.target.value);
                                setHasUnsavedChanges(true);
                                setSaveSuccess(false);
                              }}
                              rows={3}
                              className="w-full text-sm text-gray-700 bg-white border border-gray-200 rounded-md p-2 resize-none overflow-y-auto"
                              placeholder="No description"
                              spellCheck={true}
                              autoCorrect="on"
                              autoCapitalize="sentences"
                              autoComplete="one-time-code"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Desktop: Just title and description (images in left column) */}
                      <div className="hidden lg:block space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Title
                          </label>
                          <p className="text-sm font-medium text-gray-900">
                            {itemTitle}
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Description
                          </label>
                          <textarea
                            value={itemDescription}
                            onChange={(e) => {
                              setItemDescription(e.target.value);
                              setHasUnsavedChanges(true);
                              setSaveSuccess(false);
                            }}
                            rows={4}
                            className="w-full text-sm text-gray-700 bg-white border border-gray-200 rounded-md p-2 resize-none overflow-y-auto"
                            placeholder="No description"
                            spellCheck={true}
                            autoCorrect="on"
                            autoCapitalize="sentences"
                            autoComplete="one-time-code"
                          />
                        </div>
                      </div>

                      {/* Save status indicators */}
                      {updateItemMutation.isPending && (
                        <div className="mt-2 flex items-center gap-2 text-xs text-blue-600">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Saving...
                        </div>
                      )}
                      {saveSuccess && (
                        <div className="mt-2 flex items-center gap-2 text-xs text-green-600">
                          <Check className="w-3 h-3" />
                          Saved successfully
                        </div>
                      )}
                      {hasUnsavedChanges && !updateItemMutation.isPending && !saveSuccess && (
                        <div className="mt-2 text-xs text-amber-600">
                          You have unsaved changes
                        </div>
                      )}

                      {/* Location */}
                      {(item.location || item.locationImage) && (
                        <div className="mt-3 pt-3 border-t">
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            <MapPin className="w-3 h-3 inline mr-1" />
                            Location
                          </label>
                          <div className="flex items-center gap-3 p-2 bg-white rounded-lg">
                            {item.locationImage && (
                              <img
                                src={item.locationImage}
                                alt="Location"
                                className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                              />
                            )}
                            {item.location && (
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-700">{item.location}</span>
                                {item.shippedAt ? (
                                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                                    Shipped
                                  </span>
                                ) : (fullItem as any)?.locationPosition && (
                                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                                    #{(fullItem as any).locationPosition}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Already Listed Banner */}
                    {isListed && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-100 rounded-full p-2">
                            <Check className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-blue-900">Listed on Discogs</p>
                            <p className="text-sm text-blue-700">This item is already listed. You can only update the price.</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Shipping Status Section - only for sold items */}
                    {isSold && (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        {/* Collapsible Header */}
                        <button
                          type="button"
                          onClick={() => setIsShippingExpanded(!isShippingExpanded)}
                          className={`w-full p-4 flex items-center justify-between text-left transition-colors ${
                            isDelivered ? 'bg-green-50 hover:bg-green-100' :
                            isShipped ? 'bg-amber-50 hover:bg-amber-100' :
                            'bg-gray-50 hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`rounded-full p-2 ${
                              isDelivered ? 'bg-green-100' :
                              isShipped ? 'bg-amber-100' :
                              'bg-gray-200'
                            }`}>
                              {isDelivered ? (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              ) : isShipped ? (
                                <Truck className="w-5 h-5 text-amber-600" />
                              ) : (
                                <Package className="w-5 h-5 text-gray-500" />
                              )}
                            </div>
                            <div>
                              <p className={`font-medium ${
                                isDelivered ? 'text-green-900' :
                                isShipped ? 'text-amber-900' :
                                'text-gray-900'
                              }`}>
                                Shipping Status: {isDelivered ? 'Delivered' : isShipped ? 'Shipped' : 'Awaiting Shipment'}
                              </p>
                              <p className={`text-sm ${
                                isDelivered ? 'text-green-700' :
                                isShipped ? 'text-amber-700' :
                                'text-gray-600'
                              }`}>
                                {isDelivered ? 'Package has been delivered' :
                                 isShipped ? 'Package is on its way' :
                                 'Click to add tracking info'}
                              </p>
                            </div>
                          </div>
                          {isShippingExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </button>

                        {/* Expanded Content */}
                        {isShippingExpanded && (
                          <div className="p-4 border-t border-gray-200 space-y-4">
                            {/* Progress Timeline */}
                            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
                                  <Check className="w-4 h-4 text-white" />
                                </div>
                                <span className="text-sm font-medium text-red-700">Sold</span>
                              </div>
                              <div className={`flex-1 h-1 mx-3 rounded ${isShipped ? 'bg-amber-400' : 'bg-gray-300'}`} />
                              <div className="flex items-center gap-2">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                  isShipped ? 'bg-amber-500' : 'bg-gray-300'
                                }`}>
                                  {isShipped ? (
                                    <Check className="w-4 h-4 text-white" />
                                  ) : (
                                    <span className="w-2 h-2 rounded-full bg-white" />
                                  )}
                                </div>
                                <span className={`text-sm font-medium ${isShipped ? 'text-amber-700' : 'text-gray-500'}`}>
                                  Shipped
                                </span>
                              </div>
                              <div className={`flex-1 h-1 mx-3 rounded ${isDelivered ? 'bg-green-400' : 'bg-gray-300'}`} />
                              <div className="flex items-center gap-2">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                  isDelivered ? 'bg-green-500' : 'bg-gray-300'
                                }`}>
                                  {isDelivered ? (
                                    <Check className="w-4 h-4 text-white" />
                                  ) : (
                                    <span className="w-2 h-2 rounded-full bg-white" />
                                  )}
                                </div>
                                <span className={`text-sm font-medium ${isDelivered ? 'text-green-700' : 'text-gray-500'}`}>
                                  Delivered
                                </span>
                              </div>
                            </div>

                            {/* Discogs Status - Show if available and different from local */}
                            {orderInfo?.shipping && (
                              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Disc className="w-4 h-4 text-blue-600" />
                                    <span className="text-sm font-medium text-blue-900">
                                      Discogs Status: {orderInfo.shipping.status}
                                    </span>
                                  </div>
                                  {/* Show sync button if Discogs says shipped but local doesn't */}
                                  {orderInfo.shipping.status === 'Shipped' && !isShipped && (
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        try {
                                          await api.items.update(item.id, {
                                            shippedAt: orderInfo.shipping?.shippedAt ? new Date(orderInfo.shipping.shippedAt) : new Date(),
                                            trackingNumber: orderInfo.shipping?.trackingNumber || null,
                                            trackingCarrier: orderInfo.shipping?.trackingCarrier || null,
                                            shippingSource: 'discogs',
                                            listingStatus: 'sold',
                                          });
                                          queryClient.invalidateQueries({ queryKey: ['item', item.id] });
                                          queryClient.invalidateQueries({ queryKey: ['items'] });
                                        } catch (error) {
                                          console.error('Failed to sync shipping:', error);
                                        }
                                      }}
                                      className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded"
                                    >
                                      Sync to Local
                                    </button>
                                  )}
                                </div>
                                {orderInfo.shipping.trackingNumber && (
                                  <p className="text-xs text-blue-700 mt-1">
                                    Tracking: {orderInfo.shipping.trackingNumber}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Delivered State - Success Summary */}
                            {isDelivered && (
                              <div className="space-y-2">
                                {/* Discogs Source Badge */}
                                {isFromDiscogs && (
                                  <div className="flex items-center gap-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-md w-fit">
                                    <Disc className="w-3 h-3" />
                                    Synced from Discogs
                                  </div>
                                )}
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                  <div className="flex items-center gap-3">
                                    <CheckCircle className="w-8 h-8 text-green-500" />
                                    <div>
                                      <p className="font-medium text-green-900">Successfully Delivered!</p>
                                      {itemWithShipping?.trackingNumber && (
                                        <p className="text-sm text-green-700">
                                          {SHIPPING_CARRIERS.find(c => c.id === itemWithShipping?.trackingCarrier)?.name || 'Carrier'}: {itemWithShipping.trackingNumber}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Shipped State - Show tracking info with edit option */}
                            {isShipped && !isDelivered && (
                              <>
                                {isEditingShipping ? (
                                  <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Carrier</label>
                                        <select
                                          value={shippingCarrier}
                                          onChange={(e) => setShippingCarrier(e.target.value as ShippingCarrierId)}
                                          className="input text-sm"
                                        >
                                          {SHIPPING_CARRIERS.map((carrier) => (
                                            <option key={carrier.id} value={carrier.id}>{carrier.name}</option>
                                          ))}
                                        </select>
                                      </div>
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Tracking Number</label>
                                        <input
                                          type="text"
                                          value={shippingTrackingNumber}
                                          onChange={(e) => setShippingTrackingNumber(e.target.value)}
                                          placeholder="Enter tracking number"
                                          className="input text-sm"
                                        />
                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <button
                                        type="button"
                                        onClick={() => setIsEditingShipping(false)}
                                        className="btn-secondary text-sm flex-1"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        type="button"
                                        onClick={handleUpdateTracking}
                                        className="btn-primary text-sm flex-1"
                                      >
                                        Save Tracking
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {/* Discogs Source Badge */}
                                    {isFromDiscogs && (
                                      <div className="flex items-center gap-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-md w-fit">
                                        <Disc className="w-3 h-3" />
                                        Synced from Discogs
                                      </div>
                                    )}
                                    <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                                      <div>
                                        <p className="text-sm font-medium text-amber-900">
                                          {SHIPPING_CARRIERS.find(c => c.id === itemWithShipping?.trackingCarrier)?.name || 'Carrier'}
                                        </p>
                                        <p className="text-sm text-amber-700">
                                          {itemWithShipping?.trackingNumber || 'No tracking number'}
                                        </p>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => setIsEditingShipping(true)}
                                        className="text-sm text-amber-700 hover:text-amber-900 font-medium"
                                      >
                                        Edit
                                      </button>
                                    </div>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={handleMarkAsDelivered}
                                  className="w-full btn-primary bg-green-600 hover:bg-green-700"
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Mark as Delivered
                                </button>
                              </>
                            )}

                            {/* Not Shipped State - Show form to enter tracking and mark as shipped */}
                            {!isShipped && (
                              <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Carrier</label>
                                    <select
                                      value={shippingCarrier}
                                      onChange={(e) => setShippingCarrier(e.target.value as ShippingCarrierId)}
                                      className="input text-sm"
                                    >
                                      {SHIPPING_CARRIERS.map((carrier) => (
                                        <option key={carrier.id} value={carrier.id}>{carrier.name}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Tracking Number</label>
                                    <input
                                      type="text"
                                      value={shippingTrackingNumber}
                                      onChange={(e) => setShippingTrackingNumber(e.target.value)}
                                      placeholder="Enter tracking number"
                                      className="input text-sm"
                                    />
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={handleMarkAsShipped}
                                  className="w-full btn-primary bg-amber-500 hover:bg-amber-600"
                                >
                                  <Truck className="w-4 h-4 mr-2" />
                                  Mark as Shipped
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Order Info Section - only for sold items */}
                    {isSold && (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="p-4 bg-purple-50">
                          <div className="flex items-center gap-2 mb-3">
                            <MessageCircle className="w-5 h-5 text-purple-600" />
                            <h3 className="font-medium text-purple-900">Buyer & Messages</h3>
                          </div>
                          {isLoadingOrderInfo ? (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {/* Buyer Info */}
                              {orderInfo?.buyer && (
                                <div className="p-3 bg-white rounded-lg">
                                  <p className="text-xs text-gray-500 mb-1">Buyer</p>
                                  <p className="text-sm font-medium text-gray-900">{orderInfo.buyer.username}</p>
                                  {orderInfo.buyer.email && (
                                    <a
                                      href={`mailto:${orderInfo.buyer.email}`}
                                      className="text-sm text-blue-600 hover:text-blue-800"
                                    >
                                      {orderInfo.buyer.email}
                                    </a>
                                  )}
                                  {orderInfo.shippingAddress && (
                                    <p className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">
                                      {orderInfo.shippingAddress}
                                    </p>
                                  )}
                                </div>
                              )}

                              {/* Messages */}
                              {orderInfo?.messages && orderInfo.messages.length > 0 ? (
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                  <p className="text-xs text-gray-500">Messages</p>
                                  {orderInfo.messages.map((msg, idx) => (
                                    <div
                                      key={idx}
                                      className={`p-3 rounded-lg ${
                                        msg.type === 'status' ? 'bg-gray-100' :
                                        msg.type === 'shipping' ? 'bg-amber-50' :
                                        'bg-white'
                                      }`}
                                    >
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-sm font-medium text-gray-900">
                                          {msg.from.username}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {new Date(msg.timestamp).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          })}
                                        </span>
                                      </div>
                                      {msg.subject && (
                                        <p className="text-xs text-gray-600 mb-1">{msg.subject}</p>
                                      )}
                                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-purple-700">No messages for this order</p>
                              )}

                              {/* Reply Input */}
                              {orderInfo?.orderId && (
                                <div className="mt-3 pt-3 border-t border-purple-200">
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      value={replyMessage}
                                      onChange={(e) => setReplyMessage(e.target.value)}
                                      placeholder="Type a message to buyer..."
                                      className="flex-1 text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && replyMessage.trim() && !sendMessageMutation.isPending) {
                                          sendMessageMutation.mutate(replyMessage.trim());
                                        }
                                      }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => sendMessageMutation.mutate(replyMessage.trim())}
                                      disabled={!replyMessage.trim() || sendMessageMutation.isPending}
                                      className="px-3 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white rounded-lg transition-colors flex items-center gap-1"
                                    >
                                      {sendMessageMutation.isPending ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Send className="w-4 h-4" />
                                      )}
                                    </button>
                                  </div>
                                  {sendMessageMutation.isError && (
                                    <p className="text-xs text-red-600 mt-1">Failed to send message</p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Email Notifications Section - only for sold items */}
                    {isSold && (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="p-4 bg-blue-50">
                          <div className="flex items-center gap-2 mb-3">
                            <Mail className="w-5 h-5 text-blue-600" />
                            <h3 className="font-medium text-blue-900">
                              Email Notifications
                              {linkedEmails && linkedEmails.length > 0 && (
                                <span className="ml-2 text-sm font-normal text-blue-700">
                                  ({linkedEmails.length})
                                </span>
                              )}
                            </h3>
                          </div>
                          {isLoadingEmails ? (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                            </div>
                          ) : linkedEmails && linkedEmails.length > 0 ? (
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                              {linkedEmails.map((email) => {
                                const isExpanded = expandedEmailId === email.id;
                                return (
                                  <div
                                    key={email.id}
                                    className={`rounded-lg bg-white ${!email.isRead ? 'border-l-2 border-blue-500' : ''}`}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => setExpandedEmailId(isExpanded ? null : email.id)}
                                      className="w-full p-3 text-left hover:bg-gray-50 transition-colors"
                                    >
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center gap-2">
                                            <p className={`text-sm truncate ${!email.isRead ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                                              {email.subject}
                                            </p>
                                            {isExpanded ? (
                                              <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                            ) : (
                                              <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                            )}
                                          </div>
                                          {!isExpanded && email.snippet && (
                                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                              {email.snippet}
                                            </p>
                                          )}
                                        </div>
                                        <span className="text-xs text-gray-400 whitespace-nowrap">
                                          {new Date(email.receivedAt).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                          })}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2 mt-2">
                                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                                          email.provider === 'gmail'
                                            ? 'bg-red-100 text-red-700'
                                            : 'bg-blue-100 text-blue-700'
                                        }`}>
                                          {email.provider === 'gmail' ? 'Gmail' : 'Outlook'}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {email.fromName || email.fromEmail}
                                        </span>
                                      </div>
                                    </button>
                                    {isExpanded && (
                                      <div className="border-t border-gray-100">
                                        {/* Email header */}
                                        <div className="px-4 py-3 bg-gray-50 text-xs text-gray-500 space-y-1">
                                          <div className="flex gap-2">
                                            <span className="font-medium text-gray-600 w-12">From:</span>
                                            <span>{email.fromName ? `${email.fromName} <${email.fromEmail}>` : email.fromEmail}</span>
                                          </div>
                                          <div className="flex gap-2">
                                            <span className="font-medium text-gray-600 w-12">Date:</span>
                                            <span>{new Date(email.receivedAt).toLocaleString('en-US', {
                                              weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
                                              hour: 'numeric', minute: '2-digit',
                                            })}</span>
                                          </div>
                                          <div className="flex gap-2">
                                            <span className="font-medium text-gray-600 w-12">Subject:</span>
                                            <span className="text-gray-700">{email.subject}</span>
                                          </div>
                                        </div>
                                        {/* Email body */}
                                        <div className="px-1 py-1">
                                          {email.bodyHtml ? (
                                            <iframe
                                              srcDoc={email.bodyHtml}
                                              sandbox=""
                                              title={`Email: ${email.subject}`}
                                              className="w-full border-0 min-h-[200px] max-h-[400px]"
                                              style={{ overflow: 'auto' }}
                                              onLoad={(e) => {
                                                const iframe = e.target as HTMLIFrameElement;
                                                if (iframe.contentDocument?.body) {
                                                  const height = iframe.contentDocument.body.scrollHeight;
                                                  iframe.style.height = `${Math.min(height + 20, 400)}px`;
                                                }
                                              }}
                                            />
                                          ) : email.bodyText ? (
                                            <div className="px-3 py-2 text-sm text-gray-700 leading-relaxed space-y-3 max-h-72 overflow-y-auto">
                                              {email.bodyText.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split(/\n{2,}/).map((paragraph, i) => (
                                                <p key={i} className="break-words">
                                                  {paragraph.split('\n').map((line, j, arr) => (
                                                    <span key={j}>
                                                      {line}
                                                      {j < arr.length - 1 && <br />}
                                                    </span>
                                                  ))}
                                                </p>
                                              ))}
                                            </div>
                                          ) : (
                                            <p className="px-3 py-2 text-xs text-gray-400 italic">No email content available</p>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-center py-4">
                              <p className="text-sm text-blue-700">No email notifications linked</p>
                              <p className="text-xs text-blue-600 mt-1">
                                Connect Gmail or Outlook in Settings to track Discogs emails
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Share Link Section */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Collapsible Header */}
                      <button
                        type="button"
                        onClick={() => setIsShareExpanded(!isShareExpanded)}
                        className={`w-full p-4 flex items-center justify-between text-left transition-colors ${
                          shareUrl ? 'bg-emerald-50 hover:bg-emerald-100' : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`rounded-full p-2 ${shareUrl ? 'bg-emerald-100' : 'bg-gray-200'}`}>
                            <Share2 className={`w-5 h-5 ${shareUrl ? 'text-emerald-600' : 'text-gray-500'}`} />
                          </div>
                          <div>
                            <p className={`font-medium ${shareUrl ? 'text-emerald-900' : 'text-gray-900'}`}>
                              Share Link
                            </p>
                            <p className={`text-sm ${shareUrl ? 'text-emerald-700' : 'text-gray-600'}`}>
                              {shareUrl ? 'Public link is active' : 'Create a shareable link'}
                            </p>
                          </div>
                        </div>
                        {isShareExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </button>

                      {/* Expanded Content */}
                      {isShareExpanded && (
                        <div className="p-4 border-t border-gray-200 space-y-3">
                          <p className="text-sm text-gray-600">
                            Share a public link showing this item's title, description, condition, and images. Location and price are not shared.
                          </p>

                          {shareUrl ? (
                            <>
                              <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-gray-200">
                                <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <input
                                  type="text"
                                  value={shareUrl}
                                  readOnly
                                  className="flex-1 text-sm text-gray-700 bg-transparent border-none focus:ring-0 p-0"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(shareUrl);
                                    setCopiedShare(true);
                                    setTimeout(() => setCopiedShare(false), 2000);
                                  }}
                                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                    copiedShare
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                  }`}
                                >
                                  {copiedShare ? (
                                    <span className="flex items-center gap-1">
                                      <Check className="w-3.5 h-3.5" />
                                      Copied
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1">
                                      <Copy className="w-3.5 h-3.5" />
                                      Copy
                                    </span>
                                  )}
                                </button>
                              </div>
                              <div className="flex gap-2">
                                <a
                                  href={shareUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn-secondary text-sm flex-1 flex items-center justify-center gap-1"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  Preview
                                </a>
                                <button
                                  type="button"
                                  onClick={() => revokeShareLinkMutation.mutate()}
                                  disabled={revokeShareLinkMutation.isPending}
                                  className="btn-secondary text-sm flex-1 text-red-600 hover:bg-red-50 flex items-center justify-center gap-1"
                                >
                                  {revokeShareLinkMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <X className="w-4 h-4" />
                                      Revoke
                                    </>
                                  )}
                                </button>
                              </div>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => generateShareLinkMutation.mutate()}
                              disabled={generateShareLinkMutation.isPending}
                              className="w-full btn-primary bg-emerald-600 hover:bg-emerald-700 flex items-center justify-center gap-2"
                            >
                              {generateShareLinkMutation.isPending ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <Link2 className="w-4 h-4" />
                                  Generate Share Link
                                </>
                              )}
                            </button>
                          )}

                          {generateShareLinkMutation.isError && (
                            <p className="text-sm text-red-600">Failed to generate share link</p>
                          )}
                          {revokeShareLinkMutation.isError && (
                            <p className="text-sm text-red-600">Failed to revoke share link</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ImgBB Images Section */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Collapsible Header */}
                      <button
                        type="button"
                        onClick={() => setIsImgbbExpanded(!isImgbbExpanded)}
                        className={`w-full p-4 flex items-center justify-between text-left transition-colors ${
                          imgbbUrls.length > 0 ? 'bg-purple-50 hover:bg-purple-100' : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`rounded-full p-2 ${imgbbUrls.length > 0 ? 'bg-purple-100' : 'bg-gray-200'}`}>
                            <Upload className={`w-5 h-5 ${imgbbUrls.length > 0 ? 'text-purple-600' : 'text-gray-500'}`} />
                          </div>
                          <div>
                            <p className={`font-medium ${imgbbUrls.length > 0 ? 'text-purple-900' : 'text-gray-900'}`}>
                              External Images
                            </p>
                            <p className={`text-sm ${imgbbUrls.length > 0 ? 'text-purple-700' : 'text-gray-600'}`}>
                              {imgbbUrls.length > 0 ? `${imgbbUrls.length} image${imgbbUrls.length > 1 ? 's' : ''} uploaded` : 'Share images on Discogs & other platforms'}
                            </p>
                          </div>
                        </div>
                        {isImgbbExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </button>

                      {/* Expanded Content */}
                      {isImgbbExpanded && (
                        <div className="p-4 border-t border-gray-200 space-y-3">
                          <p className="text-sm text-gray-600">
                            Upload images to ImgBB to share on Discogs and other platforms that block findmystuff.ai links.
                          </p>

                          {imgbbUrls.length > 0 ? (
                            <>
                              <div className="bg-white rounded-lg border border-gray-200 p-3">
                                <textarea
                                  readOnly
                                  value={`Photos of this item:\n${imgbbUrls.map((url, i) => `${i + 1}. ${url}`).join('\n')}`}
                                  className="w-full text-sm text-gray-700 bg-transparent border-none focus:ring-0 p-0 resize-none"
                                  rows={Math.min(imgbbUrls.length + 1, 6)}
                                />
                              </div>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const message = `Photos of this item:\n${imgbbUrls.map((url, i) => `${i + 1}. ${url}`).join('\n')}`;
                                    navigator.clipboard.writeText(message);
                                    setCopiedImgbbIndex(0);
                                    setTimeout(() => setCopiedImgbbIndex(null), 2000);
                                  }}
                                  className={`btn-primary flex-1 flex items-center justify-center gap-2 ${
                                    copiedImgbbIndex === 0
                                      ? 'bg-purple-700'
                                      : 'bg-purple-600 hover:bg-purple-700'
                                  }`}
                                >
                                  {copiedImgbbIndex === 0 ? (
                                    <>
                                      <Check className="w-4 h-4" />
                                      Copied!
                                    </>
                                  ) : (
                                    <>
                                      <Copy className="w-4 h-4" />
                                      Copy Message
                                    </>
                                  )}
                                </button>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => uploadToImgbbMutation.mutate()}
                                  disabled={uploadToImgbbMutation.isPending}
                                  className="btn-secondary text-sm flex-1 flex items-center justify-center gap-1"
                                  title="Re-upload images"
                                >
                                  {uploadToImgbbMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <RefreshCw className="w-4 h-4" />
                                      Re-upload
                                    </>
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deleteImgbbImagesMutation.mutate()}
                                  disabled={deleteImgbbImagesMutation.isPending}
                                  className="btn-secondary text-sm text-red-600 hover:bg-red-50 flex items-center justify-center gap-1"
                                  title="Clear images"
                                >
                                  {deleteImgbbImagesMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <Trash2 className="w-4 h-4" />
                                      Clear
                                    </>
                                  )}
                                </button>
                              </div>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => uploadToImgbbMutation.mutate()}
                              disabled={uploadToImgbbMutation.isPending || allImages.length === 0}
                              className="w-full btn-primary bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
                            >
                              {uploadToImgbbMutation.isPending ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <Upload className="w-4 h-4" />
                                  Upload to ImgBB
                                </>
                              )}
                            </button>
                          )}

                          {allImages.length === 0 && imgbbUrls.length === 0 && (
                            <p className="text-sm text-amber-600">Add images to this item first</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Release Search */}
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {isListed ? 'Discogs Release' : 'Search Discogs Release *'}
                      </label>
                      {isListed ? (
                        <div className="input pl-10 bg-gray-50 text-gray-600 cursor-not-allowed">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          {releaseSearch}
                        </div>
                      ) : (
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={releaseSearch}
                            onChange={(e) => {
                              setReleaseSearch(e.target.value);
                              setSelectedRelease(null);
                              setShowSearchResults(true);
                            }}
                            onFocus={() => setShowSearchResults(true)}
                            placeholder="Search for artist, album, or release..."
                            className="input pl-10"
                            required
                          />
                          {isSearching && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
                          )}
                        </div>
                      )}

                      {/* Selected Release */}
                      {selectedRelease && (
                        <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-start gap-3">
                            {selectedRelease.thumb && (
                              <img
                                src={selectedRelease.thumb}
                                alt={selectedRelease.title}
                                className="w-12 h-12 object-cover rounded"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">
                                {selectedRelease.artist} - {selectedRelease.title}
                              </p>
                              <p className="text-sm text-gray-600">
                                {selectedRelease.year && `${selectedRelease.year} · `}
                                {selectedRelease.format.join(', ')}
                              </p>
                              <p className="text-xs text-green-700 font-mono mt-1">
                                Discogs ID: {selectedRelease.id}
                                {(selectedRelease.label.length > 0 || selectedRelease.catno) && (
                                  <span>
                                    {' · '}
                                    {selectedRelease.label.length > 0 && selectedRelease.label.join(', ')}
                                    {selectedRelease.label.length > 0 && selectedRelease.catno && ' · '}
                                    {selectedRelease.catno}
                                  </span>
                                )}
                              </p>
                            </div>
                            <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                          </div>
                        </div>
                      )}

                      {/* Search Results List */}
                      {showSearchResults && searchResults.length > 0 && !selectedRelease && (() => {
                        const INITIAL_RESULTS = 4;
                        const visibleResults = showAllResults ? searchResults : searchResults.slice(0, INITIAL_RESULTS);
                        const hiddenCount = searchResults.length - INITIAL_RESULTS;

                        return (
                          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                            {/* Results count header */}
                            <div className="bg-blue-50 px-3 py-2 border-b border-blue-200 flex items-center justify-between rounded-t-lg">
                              <span className="text-sm font-medium text-blue-800">
                                {searchResults.length} {searchResults.length === 1 ? 'result' : 'results'} found
                              </span>
                              <span className="text-xs text-blue-600">Click to select</span>
                            </div>
                            {/* List of results */}
                            <div className="divide-y divide-gray-100">
                              {visibleResults.map((release) => (
                                <button
                                  key={release.id}
                                  type="button"
                                  onClick={() => handleSelectRelease(release)}
                                  className="w-full p-3 flex items-start gap-3 hover:bg-blue-50 text-left transition-colors"
                                >
                                  {release.thumb ? (
                                    <img
                                      src={release.thumb}
                                      alt={release.title}
                                      className="w-24 h-24 object-cover rounded flex-shrink-0"
                                    />
                                  ) : (
                                    <div className="w-24 h-24 bg-gray-100 rounded flex-shrink-0 flex items-center justify-center">
                                      <Disc className="w-8 h-8 text-gray-400" />
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0 py-1">
                                    <p className="font-medium text-gray-900 text-sm">
                                      {release.artist} - {release.title}
                                    </p>
                                    <p className="text-xs text-gray-600 mt-1">
                                      {release.year && <span className="font-semibold">{release.year}</span>}
                                      {release.year && release.country && ' · '}
                                      {release.country && <span>{release.country}</span>}
                                      {(release.year || release.country) && release.format.length > 0 && ' · '}
                                      {release.format.slice(0, 2).join(', ')}
                                    </p>
                                    {release.label.length > 0 && (
                                      <p className="text-xs text-gray-500 mt-0.5">
                                        {release.label[0]}
                                        {release.catno && <span className="font-mono ml-1">({release.catno})</span>}
                                      </p>
                                    )}
                                    {/* Release ID */}
                                    <p className="text-xs text-gray-400 mt-1 font-mono">
                                      Release #{release.id}
                                    </p>
                                  </div>
                                </button>
                              ))}
                            </div>
                            {/* Show more button */}
                            {hiddenCount > 0 && !showAllResults && (
                              <button
                                type="button"
                                onClick={() => setShowAllResults(true)}
                                className="w-full py-3 px-4 bg-gray-50 hover:bg-gray-100 text-sm font-medium text-blue-600 hover:text-blue-700 border-t border-gray-200 rounded-b-lg transition-colors flex items-center justify-center gap-2"
                              >
                                <ChevronDown className="w-4 h-4" />
                                Show {hiddenCount} more {hiddenCount === 1 ? 'result' : 'results'}
                              </button>
                            )}
                            {/* Collapse button when expanded */}
                            {showAllResults && searchResults.length > INITIAL_RESULTS && (
                              <button
                                type="button"
                                onClick={() => setShowAllResults(false)}
                                className="w-full py-3 px-4 bg-gray-50 hover:bg-gray-100 text-sm font-medium text-gray-600 hover:text-gray-700 border-t border-gray-200 rounded-b-lg transition-colors flex items-center justify-center gap-2"
                              >
                                <ChevronUp className="w-4 h-4" />
                                Show less
                              </button>
                            )}
                          </div>
                        );
                      })()}

                      {/* No results message */}
                      {showSearchResults && releaseSearch.length >= 3 && !isSearching && searchResults.length === 0 && !selectedRelease && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500 text-sm">
                          No releases found. Try a different search term.
                        </div>
                      )}
                    </div>

                    {/* Instructions when no release selected (only for new listings) */}
                    {!selectedRelease && !isListed && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                        <Disc className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                        <p className="text-sm text-amber-800">
                          Search and select a Discogs release to continue.
                        </p>
                        <p className="text-xs text-amber-600 mt-1">
                          All Discogs listings must be matched to a catalog entry.
                        </p>
                      </div>
                    )}

                    {/* Condition and Price - only show after release selected */}
                    {selectedRelease && (
                      <>
                        {/* Media Condition - always editable */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Media Condition *
                          </label>
                          <select
                            value={mediaCondition}
                            onChange={(e) => {
                              setMediaCondition(e.target.value as DiscogsMediaCondition);
                              setHasUnsavedChanges(true);
                              setSaveSuccess(false);
                            }}
                            className="input"
                          >
                            {DISCOGS_MEDIA_CONDITIONS.map((cond) => (
                              <option key={cond.id} value={cond.id}>
                                {cond.name}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            {DISCOGS_MEDIA_CONDITIONS.find(c => c.id === mediaCondition)?.description}
                          </p>
                        </div>

                        {/* Sleeve Condition - always editable */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Sleeve Condition *
                          </label>
                          <select
                            value={sleeveCondition}
                            onChange={(e) => {
                              setSleeveCondition(e.target.value as DiscogsSleeveCondition);
                              setHasUnsavedChanges(true);
                              setSaveSuccess(false);
                            }}
                            className="input"
                          >
                            {DISCOGS_SLEEVE_CONDITIONS.map((cond) => (
                              <option key={cond.id} value={cond.id}>
                                {cond.name}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            {DISCOGS_SLEEVE_CONDITIONS.find(c => c.id === sleeveCondition)?.description}
                          </p>
                        </div>

                        {/* Price - always editable */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Price (USD) *
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              value={price}
                              onChange={(e) => {
                                setPrice(e.target.value);
                                setHasUnsavedChanges(true);
                                setSaveSuccess(false);
                              }}
                              min="0.01"
                              step="0.01"
                              placeholder={isLoadingPrices ? 'Loading suggested price...' : 'Enter price'}
                              className="input"
                              required
                              disabled={isLoadingPrices}
                            />
                            {isLoadingPrices && (
                              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
                            )}
                          </div>
                          {priceSuggestions.length > 0 && !isListed && (
                            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex items-center gap-1.5 text-xs text-green-700">
                                <DollarSign className="w-3.5 h-3.5" />
                                <span className="font-medium">Suggested price based on recent Discogs sales</span>
                              </div>
                            </div>
                          )}
                          {!isLoadingPrices && priceSuggestions.length === 0 && selectedRelease && !isListed && (
                            <p className="text-xs text-gray-500 mt-1">
                              No price history available for this release
                            </p>
                          )}
                        </div>

                        {/* Comments */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Comments {!isListed && '(optional)'}
                          </label>
                          {isListed ? (
                            <div className="input bg-gray-50 text-gray-600 cursor-not-allowed min-h-[80px] whitespace-pre-wrap">
                              {comments || <span className="text-gray-400 italic">No comments</span>}
                            </div>
                          ) : (
                            <textarea
                              value={comments}
                              onChange={(e) => {
                                setComments(e.target.value);
                                setHasUnsavedChanges(true);
                                setSaveSuccess(false);
                              }}
                              rows={3}
                              placeholder="Additional notes about this item..."
                              className="input"
                              spellCheck={true}
                              autoCorrect="on"
                              autoCapitalize="sentences"
                              autoComplete="one-time-code"
                            />
                          )}
                        </div>
                      </>
                    )}

                    {/* Error */}
                    {createListingMutation.isError && (
                      <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>
                          {createListingMutation.error instanceof Error
                            ? createListingMutation.error.message
                            : 'Failed to create listing'}
                        </span>
                      </div>
                    )}
                  </form>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex gap-2 p-4 border-t sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={handleClose}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!hasUnsavedChanges || updateItemMutation.isPending}
                className="btn-secondary flex-1 flex items-center justify-center gap-1"
              >
                {updateItemMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save
                  </>
                )}
              </button>
              {isListed ? (
                <>
                  {listingHasChanges ? (
                    <button
                      type="button"
                      onClick={() => updateListingMutation.mutate()}
                      disabled={updateListingMutation.isPending}
                      className="btn-primary flex-1 flex items-center justify-center gap-2"
                    >
                      {updateListingMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Update on Discogs
                        </>
                      )}
                    </button>
                  ) : (
                    <a
                      href={listingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary flex-1 flex items-center justify-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View on Discogs
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={handleDelist}
                    disabled={deleteListingMutation.isPending}
                    className="px-4 py-2 bg-red-100 text-red-600 hover:bg-red-200 rounded-lg font-medium flex items-center gap-2 transition-colors"
                  >
                    {deleteListingMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    Delist
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!isValid || createListingMutation.isPending || updateItemMutation.isPending}
                  className="btn-primary flex-1"
                >
                  {createListingMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Publishing...
                    </>
                  ) : (
                    'List on Discogs'
                  )}
                </button>
              )}
            </div>
        </div>
      </div>

      {/* Image Lightbox */}
      {showImageLightbox && allImages.length > 0 && (
        <ImageLightbox
          images={allImages}
          initialIndex={lightboxImageIndex}
          onClose={() => setShowImageLightbox(false)}
          title={itemTitle}
          itemId={item.id}
        />
      )}

      {/* Edit Item Modal */}
      <ItemEditModal
        itemId={item.id}
        isOpen={showEditModal}
        onClose={handleEditModalClose}
      />
    </div>
  );
}
